import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ELayoutManagerMode, EWidgetRegion } from '#shared';

import type {
  WidgetId,
  WidgetInstance,
  WidgetRegistry,
  WidgetType,
} from './widgetRegistry';
import type { UniqueIdentifier } from '@dnd-kit/core';

export interface WidgetManagerState {
  draggingWidget:
    | {
        type: 'widget-instance';
        widget: WidgetInstance;
        id: WidgetId;
      }
    | {
        type: 'widget-prototype';
        id: WidgetType;
      }
    | null;

  highlightOn: boolean;
  rotationOn: boolean;
  windRotationOn: boolean;
  weatherAnimationOn: boolean;
  carouselMs: number;
  globalSearch: string;
  userIdleMs: number;
  preferredSorting: 'asc' | 'desc';
  highlightedAircraftRegistration: string | null;
  highlightedAirports: string[];
  layoutManagerMode: ELayoutManagerMode;
  widgets: Record<WidgetId, WidgetState>;
}

const initialState: WidgetManagerState = {
  draggingWidget: null,
  highlightOn: true,
  rotationOn: false,
  windRotationOn: false,
  // Default ON: the wind map's forecast time-lapse auto-starts on first load
  // (TSMED-949). No settings persist, so initialState IS the first-load state.
  weatherAnimationOn: true,
  carouselMs: 5000,
  globalSearch: '',
  userIdleMs: 5000,
  preferredSorting: 'asc',
  highlightedAircraftRegistration: null,
  highlightedAirports: [],
  layoutManagerMode: ELayoutManagerMode.RENDER,
  widgets: {},
};

export interface WidgetState<T extends WidgetType = WidgetType> {
  id: WidgetId<T>;
  widgetType: T;
  settings: Extract<WidgetRegistry[T], { type: T }>;
  allocation: [EWidgetRegion, UniqueIdentifier, number];
}

export const widgetManagerSlice = createSlice({
  name: 'widgets/manager',
  initialState,
  reducers: {
    // Equality guards below prevent no-op dispatches from producing new state references.
    // React 19's useSyncExternalStore triggers forceStoreRerender on every snapshot change,
    // so effects that dispatch on mount (e.g. setGeneralSettings from LayoutManager) would
    // cascade into "Maximum update depth exceeded" without these.
    setGeneralSettings: (s, a: PayloadAction<Partial<WidgetManagerState>>) => {
      let changed = false;
      for (const key of Object.keys(
        a.payload,
      ) as (keyof WidgetManagerState)[]) {
        if (s[key] !== a.payload[key]) {
          changed = true;
          break;
        }
      }
      if (changed) {
        Object.assign(s, a.payload);
      }
    },
    setDraggingWidget: (
      s,
      a: PayloadAction<WidgetManagerState['draggingWidget']>,
    ) => {
      s.draggingWidget = a.payload;
    },
    setHighlightOn: (s, a: PayloadAction<boolean>) => {
      s.highlightOn = a.payload;
    },
    setRotationOn: (s, a: PayloadAction<boolean>) => {
      s.rotationOn = a.payload;
    },
    setWindRotationOn: (s, a: PayloadAction<boolean>) => {
      s.windRotationOn = a.payload;
    },
    setWeatherAnimationOn: (s, a: PayloadAction<boolean>) => {
      s.weatherAnimationOn = a.payload;
    },
    setCarouselMs: (s, a: PayloadAction<number>) => {
      s.carouselMs = a.payload;
    },
    setGlobalSearch: (s, a: PayloadAction<string>) => {
      s.globalSearch = a.payload;
    },
    setUserIdleMs: (s, a: PayloadAction<number>) => {
      s.userIdleMs = a.payload;
    },
    setPreferredSorting: (s, a: PayloadAction<'asc' | 'desc'>) => {
      s.preferredSorting = a.payload;
    },
    setLayoutManagerMode: (s, a: PayloadAction<ELayoutManagerMode>) => {
      if (s.layoutManagerMode === a.payload) {
        return;
      }
      s.layoutManagerMode = a.payload;
    },
    setHighlightedAircraftRegistration: (
      s,
      a: PayloadAction<string | null>,
    ) => {
      if (s.highlightedAircraftRegistration === a.payload) {
        return;
      }
      s.highlightedAircraftRegistration = a.payload;
    },
    setHighlightedAirports: (s, a: PayloadAction<string[]>) => {
      if (
        s.highlightedAirports.length === a.payload.length &&
        s.highlightedAirports.every((v, i) => v === a.payload[i])
      ) {
        return;
      }
      s.highlightedAirports = a.payload;
    },
  },
});

export const widgetManagerActions = widgetManagerSlice.actions;
